import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Button, ButtonGroup, IconButton, Dialog } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';
import CompeteInfoModal from '../CompeteInfoModal';

const normalizeMarkdownInput = (value) => String(value || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/^\s*---\s+(.+)$/gm, '- $1')
    .replace(/^\s*\$\$\s+(.+)$/gm, '- $1');

const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const inlineMarkdownToHtml = (value) => String(value || '')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');

const parseTwoColumnRow = (line) => {
    if (!line || (!line.includes('//') && !line.includes('|'))) return null;

    let normalizedLine = line.trim();
    if (
        (normalizedLine.startsWith('**') && normalizedLine.endsWith('**')) ||
        (normalizedLine.startsWith('__') && normalizedLine.endsWith('__'))
    ) {
        normalizedLine = normalizedLine.slice(2, -2).trim();
    }

    let delimiter = '//';
    let splitIndex = normalizedLine.indexOf('//');
    if (splitIndex === -1) {
        delimiter = '|';
        splitIndex = normalizedLine.indexOf('|');
        if (/^\|?[\s:\-]+(\|[\s:\-]+)+\|?$/.test(normalizedLine)) return null;
    }
    if (splitIndex <= 0 || splitIndex >= normalizedLine.length - delimiter.length) return null;

    const left = normalizedLine.slice(0, splitIndex).trim();
    const right = normalizedLine.slice(splitIndex + delimiter.length).trim();
    if (!left || !right) return null;

    return {
        left: inlineMarkdownToHtml(escapeHtml(left)),
        right: inlineMarkdownToHtml(escapeHtml(right))
    };
};

const markdownToHtml = (value) => {
    const normalized = normalizeMarkdownInput(value).trim();
    if (!normalized) return '';

    const lines = normalized.split('\n');
    const html = [];
    let listType = null;
    let slashTableRows = [];

    const flushList = () => {
        if (!listType) return;
        html.push(`</${listType}>`);
        listType = null;
    };

    const flushSlashTable = () => {
        if (!slashTableRows.length) return;
        html.push('<table><tbody>');
        slashTableRows.forEach((row) => {
            html.push(`<tr><td>${row.left}</td><td>${row.right}</td></tr>`);
        });
        html.push('</tbody></table>');
        slashTableRows = [];
    };

    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line) {
            flushList();
            flushSlashTable();
            continue;
        }

        const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
            flushList();
            flushSlashTable();
            const level = headingMatch[1].length;
            html.push(`<h${level}>${inlineMarkdownToHtml(escapeHtml(headingMatch[2]))}</h${level}>`);
            continue;
        }

        if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
            flushList();
            flushSlashTable();
            html.push('<hr>');
            continue;
        }

        const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
        if (unorderedMatch) {
            flushSlashTable();
            if (listType !== 'ul') {
                flushList();
                listType = 'ul';
                html.push('<ul>');
            }
            html.push(`<li>${inlineMarkdownToHtml(escapeHtml(unorderedMatch[1]))}</li>`);
            continue;
        }

        const orderedMatch = line.match(/^\d+[.)]\s+(.+)$/);
        if (orderedMatch) {
            flushSlashTable();
            if (listType !== 'ol') {
                flushList();
                listType = 'ol';
                html.push('<ol>');
            }
            html.push(`<li>${inlineMarkdownToHtml(escapeHtml(orderedMatch[1]))}</li>`);
            continue;
        }

        const slashRow = parseTwoColumnRow(line);
        if (slashRow) {
            flushList();
            slashTableRows.push(slashRow);
            continue;
        }

        flushList();
        flushSlashTable();
        html.push(`<p>${inlineMarkdownToHtml(escapeHtml(line))}</p>`);
    }

    flushList();
    flushSlashTable();
    return html.join('');
};

const toSanitizedRichHtml = (content) => {
    if (!content) return '';
    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(content);
    const normalized = hasHtml ? content : markdownToHtml(content);
    return DOMPurify.sanitize(normalized, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'span', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'a', 'code', 's', 'table', 'tbody', 'tr', 'td'],
        ALLOWED_ATTR: ['style', 'href', 'target', 'rel'],
        ALLOWED_STYLES: {
            '*': {
                color: [/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/]
            }
        }
    });
};

export default function WorkoutInfo({
    cls,
    workYourWeaknessContent = '',
    toggleWorkoutInfoFullScreen,
    closeWorkoutInfoFullScreen,
    handleAddClassToMyTrainings,
    userRole,
    showStretchingAtHomeWyw = true,
    hasCompatiblePlans = false,
    workoutImage = null
}) {
    const [selectedTrack, setSelectedTrack] = useState('rx');
    const selectedTrackRef = useRef(selectedTrack);
    const [isWywOpen, setWywOpen] = useState(false);
    const [competeModalOpen, setCompeteModalOpen] = useState(false);
    const [isImageDialogOpen, setImageDialogOpen] = useState(false);
    const [videosModalOpen, setVideosModalOpen] = useState(false);

    useEffect(() => {
        selectedTrackRef.current = selectedTrack;
    }, [selectedTrack]);

    const trackMap = useMemo(() => ({
        rx: { label: 'RX', value: cls?.description || '' },
        intermediate: { label: 'INT', value: cls?.descriptionIntermediate || '' },
        beginner: { label: 'BEG', value: cls?.descriptionBeginner || '' },
        stretching: { label: 'Stretching', value: cls?.descriptionStretching || '' },
        atHome: { label: 'At Home', value: cls?.descriptionAtHome || '' }
    }), [cls]);

    const visibleTopTracks = ['rx', 'intermediate', 'beginner'].filter((key) => trackMap[key].value);
    const visibleBottomTracks = showStretchingAtHomeWyw
        ? ['stretching', 'atHome'].filter((key) => trackMap[key].value)
        : [];
    const effectiveWywContent = showStretchingAtHomeWyw ? workYourWeaknessContent : '';
    const hasAnyTrack = [...visibleTopTracks, ...visibleBottomTracks].length > 0;

    useEffect(() => {
        if (!hasAnyTrack) return;
        if (visibleTopTracks.includes(selectedTrack) || visibleBottomTracks.includes(selectedTrack)) return;
        setSelectedTrack(visibleTopTracks[0] || visibleBottomTracks[0]);
    }, [hasAnyTrack, selectedTrack, visibleTopTracks, visibleBottomTracks]);

    const activeTrackValue = trackMap[selectedTrack]?.value || '';

    const hasCompetitionInfo = !!(cls.competitionInfo && String(cls.competitionInfo).trim());
    const classVideoLinks = useMemo(() => {
        if (Array.isArray(cls?.videoLinks)) {
            return cls.videoLinks.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
        }
        if (typeof cls?.videoLinks === "string") {
            try {
                const parsed = JSON.parse(cls.videoLinks);
                if (Array.isArray(parsed)) {
                    return parsed.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
                }
            } catch (error) {
                return [];
            }
        }
        return [];
    }, [cls?.videoLinks]);

    const getYouTubeEmbedUrl = (url) => {
        try {
            const parsed = new URL(url);
            const host = parsed.hostname.replace("www.", "");
            if (host === "youtu.be") {
                const id = parsed.pathname.slice(1);
                return id ? `https://www.youtube.com/embed/${id}` : null;
            }
            if (host === "youtube.com" || host === "m.youtube.com") {
                if (parsed.pathname === "/watch") {
                    const id = parsed.searchParams.get("v");
                    return id ? `https://www.youtube.com/embed/${id}` : null;
                }
                if (parsed.pathname.startsWith("/shorts/")) {
                    const id = parsed.pathname.split("/shorts/")[1]?.split("/")[0];
                    return id ? `https://www.youtube.com/embed/${id}` : null;
                }
                if (parsed.pathname.startsWith("/embed/")) {
                    const id = parsed.pathname.split("/embed/")[1]?.split("/")[0];
                    return id ? `https://www.youtube.com/embed/${id}` : null;
                }
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    if (
        !cls.wodName
        && !cls.wodType
        && !hasAnyTrack
        && classVideoLinks.length === 0
        && !workoutImage?.imageData
        && !effectiveWywContent
        && !hasCompetitionInfo
    ) {
        return null;
    }

    const richContentSx = {
        color: 'text.primary',
        fontSize: '0.875rem',
        lineHeight: 1.65,
        '& p': { margin: 0 },
        '& p + p': { mt: 0.5 },
        '& ul, & ol': { pl: 2, my: 0.5 },
        '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 0.4, mb: 0.6, fontSize: '0.9rem' },
        '& code': { bgcolor: '#eeeeee', px: 0.5, borderRadius: 0.5, fontFamily: 'monospace' },
        '& table': { width: '100%', borderCollapse: 'collapse', mt: 0.5, mb: 0.5 },
        '& td': { p: '6px 10px', border: 'none', verticalAlign: 'top' },
        '& tr:nth-of-type(odd)': { bgcolor: 'rgba(0, 0, 0, 0.06)' },
        '& tr:nth-of-type(even)': { bgcolor: 'transparent' },
        '& tr:first-of-type td': { fontWeight: 700 }
    };

    const AddToTrainingsButton = () => (
        userRole === "regular" && (cls.wodName || cls.wodType || hasAnyTrack) ? (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation();
                        const track = hasAnyTrack ? selectedTrackRef.current : 'rx';
                        handleAddClassToMyTrainings(track);
                    }}
                    color="primary"
                    size="small"
                    sx={{
                        border: '1px solid',
                        borderColor: 'primary.main',
                        '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        }
                    }}
                >
                    <AddIcon />
                </IconButton>
            </Box>
        ) : null
    );

    const TrackSwitches = ({ compact = false }) => (
        <>
            {visibleTopTracks.length > 1 && (
                <ButtonGroup
                    fullWidth
                    size="small"
                    sx={{ mb: 1.5 }}
                >
                    {visibleTopTracks.map((key) => (
                        <Button
                            key={key}
                            variant={selectedTrack === key ? 'contained' : 'outlined'}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTrack(key);
                            }}
                        >
                            {trackMap[key].label}
                        </Button>
                    ))}
                </ButtonGroup>
            )}

            {!!activeTrackValue && (
                <Box sx={{ pb: 2, ...richContentSx }}>
                    <div
                        dangerouslySetInnerHTML={{ __html: toSanitizedRichHtml(activeTrackValue) }}
                    />
                </Box>
            )}

            {visibleTopTracks.length > 0 && visibleBottomTracks.length === 0 && <AddToTrainingsButton />}

            {visibleBottomTracks.length > 0 && (
                <>
                    <AddToTrainingsButton />
                    <ButtonGroup
                        fullWidth
                        size="small"
                        sx={{ mt: compact ? 1 : 0, mb: 1.5 }}
                    >
                        {visibleBottomTracks.map((key) => (
                            <Button
                                key={key}
                                variant={selectedTrack === key ? "contained" : "outlined"}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTrack(key);
                                }}
                            >
                                {trackMap[key].label}
                            </Button>
                        ))}
                    </ButtonGroup>
                </>
            )}
        </>
    );

    const WorkYourWeaknessButton = () => (
        effectiveWywContent ? (
            <Button
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1.5 }}
                onClick={(e) => {
                    e.stopPropagation();
                    closeWorkoutInfoFullScreen?.(); // Sulge täisekraan, et modal näitaks ainult oma sisu
                    setWywOpen(true);
                }}
            >
                Work Your Weakness
            </Button>
        ) : null
    );

    const isAffiliateOrTrainer = userRole === 'affiliate' || userRole === 'trainer';
    const showCompeteButton = hasCompetitionInfo && (
        isAffiliateOrTrainer || (userRole === 'regular' && hasCompatiblePlans)
    );
    const CompeteButton = () => (
        showCompeteButton ? (
            <Button
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1.5 }}
                onClick={(e) => {
                    e.stopPropagation();
                    closeWorkoutInfoFullScreen?.(); // Sulge täisekraan, et Compete modal näitaks ainult compete infot
                    setCompeteModalOpen(true);
                }}
            >
                Compete
            </Button>
        ) : null
    );
    const VideosButton = () => (
        classVideoLinks.length > 0 ? (
            <Button
                fullWidth
                size="small"
                variant="outlined"
                sx={{ mt: 1.5 }}
                onClick={(e) => {
                    e.stopPropagation();
                    closeWorkoutInfoFullScreen?.();
                    setVideosModalOpen(true);
                }}
            >
                Videos
            </Button>
        ) : null
    );

    return (
        <>
            {/* Desktop version */}
            <Box
                sx={{
                    display: { xs: 'none', sm: 'block' },
                    mb: 2
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "#ccc",
                        padding: 2,
                        borderRadius: "8px",
                        position: "relative",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            backgroundColor: "#bbb",
                        }
                    }}
                    onClick={toggleWorkoutInfoFullScreen}
                >
                    {/* Fullscreen icon */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "rgba(0,0,0,0.1)",
                            borderRadius: "50%",
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                    >
                        <FullscreenIcon fontSize="small" />
                    </Box>

                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", marginBottom: 1 }}
                    >
                        Workout Info
                    </Typography>
                    {cls.wodName && (
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>
                            <strong>🔥{cls.wodName}</strong>
                        </Typography>
                    )}
                    {cls.wodType && cls.wodType !== "NONE" && (
                        <Typography sx={{ color: "secondary.main", mb: 1, fontStyle: "italic" }}>
                            <strong>{cls.wodType}</strong>
                        </Typography>
                    )}
                    {!!workoutImage?.imageData && (
                        <Box sx={{ mb: 1.5 }} onClick={(e) => e.stopPropagation()}>
                            <Box
                                component="img"
                                src={workoutImage.imageData}
                                alt="Workout"
                                onClick={() => setImageDialogOpen(true)}
                                sx={{
                                    width: "100%",
                                    maxHeight: 220,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid rgba(0, 0, 0, 0.2)",
                                    cursor: "zoom-in"
                                }}
                            />
                        </Box>
                    )}
                    {hasAnyTrack && <TrackSwitches />}
                    {/* Plus button when no tracks but has wodName/wodType */}
                    {userRole === "regular" && !hasAnyTrack && (cls.wodName || cls.wodType) && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddClassToMyTrainings('rx');
                                }}
                                color="primary"
                                size="small"
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                    }
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    )}
                    {/* Wrapper: vältida täisekraani avamist Compete/WYW nuppude vajutamisel */}
                    <Box onClick={(e) => e.stopPropagation()}>
                        <WorkYourWeaknessButton />
                        <VideosButton />
                        <CompeteButton />
                    </Box>
                </Box>
            </Box>

            {/* Mobile version */}
            <Box
                sx={{
                    display: { xs: 'block', sm: 'none' },
                    mb: 2
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "#ccc",
                        padding: 2,
                        borderRadius: "8px",
                        position: "relative",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            backgroundColor: "#bbb",
                        }
                    }}
                    onClick={toggleWorkoutInfoFullScreen}
                >
                    <Box sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(0,0,0,0.1)",
                        borderRadius: "50%",
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <FullscreenIcon fontSize="small" />
                    </Box>

                    <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", marginBottom: 1 }}
                    >
                        Workout Info
                    </Typography>
                    {cls.wodName && (
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>
                            <strong>🔥{cls.wodName}</strong>
                        </Typography>
                    )}
                    {cls.wodType && cls.wodType !== "NONE" && (
                        <Typography sx={{ color: "secondary.main", mb: 1, fontStyle: "italic" }}>
                            <strong>{cls.wodType}</strong>
                        </Typography>
                    )}
                    {!!workoutImage?.imageData && (
                        <Box sx={{ mb: 1.5 }} onClick={(e) => e.stopPropagation()}>
                            <Box
                                component="img"
                                src={workoutImage.imageData}
                                alt="Workout"
                                onClick={() => setImageDialogOpen(true)}
                                sx={{
                                    width: "100%",
                                    maxHeight: 220,
                                    objectFit: "cover",
                                    borderRadius: 1,
                                    border: "1px solid rgba(0, 0, 0, 0.2)",
                                    cursor: "zoom-in"
                                }}
                            />
                        </Box>
                    )}
                    {hasAnyTrack && <TrackSwitches compact />}
                    {/* Plus button (mobile) when no tracks but has wodName/wodType */}
                    {userRole === "regular" && !hasAnyTrack && (cls.wodName || cls.wodType) && (
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddClassToMyTrainings('rx');
                                }}
                                color="primary"
                                size="small"
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                    }
                                }}
                            >
                                <AddIcon />
                            </IconButton>
                        </Box>
                    )}
                    {/* Wrapper: vältida täisekraani avamist Compete/WYW nuppude vajutamisel */}
                    <Box onClick={(e) => e.stopPropagation()}>
                        <WorkYourWeaknessButton />
                        <VideosButton />
                        <CompeteButton />
                    </Box>
                </Box>
            </Box>

            <CompeteInfoModal
                open={isWywOpen}
                onClose={() => setWywOpen(false)}
                content={effectiveWywContent || ''}
                title="Work Your Weakness"
                emptyMessage="No Work Your Weakness info for this class."
                normalizeRestDay={false}
                dialogProps={{ slotProps: { root: { sx: { zIndex: 2100 } } } }}
            />

            <CompeteInfoModal
                open={competeModalOpen}
                onClose={() => setCompeteModalOpen(false)}
                content={cls?.competitionInfo || ''}
                title="Compete"
                dialogProps={{ slotProps: { root: { sx: { zIndex: 2100 } } } }}
                showAddToTrainings={userRole === 'regular' && hasCompatiblePlans}
                onAddToTrainings={handleAddClassToMyTrainings ? () => handleAddClassToMyTrainings('rx', true) : undefined}
            />

            <Dialog
                open={videosModalOpen}
                onClose={() => setVideosModalOpen(false)}
                maxWidth="md"
                fullWidth
                slotProps={{ root: { sx: { zIndex: 2100 } } }}
                PaperProps={{
                    sx: {
                        m: { xs: 0 },
                        width: { xs: "100%" },
                        maxWidth: { xs: "100%" },
                        borderRadius: { xs: 0 }
                    }
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Class Videos
                        </Typography>
                        <IconButton onClick={() => setVideosModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {classVideoLinks.map((link, index) => {
                        const embedUrl = getYouTubeEmbedUrl(link);
                        if (!embedUrl) return null;
                        return (
                            <Box key={`class-video-${index}`} sx={{ mb: 2 }}>
                                <Box
                                    component="iframe"
                                    src={embedUrl}
                                    title={`class-video-${index}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    sx={{
                                        width: "100%",
                                        height: { xs: 220, sm: 360 },
                                        border: 0,
                                        borderRadius: 1
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            </Dialog>

            <Dialog
                open={isImageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="lg"
                fullWidth
                slotProps={{ root: { sx: { zIndex: 2200 } } }}
            >
                <Box sx={{ position: "relative", backgroundColor: "black" }}>
                    <IconButton
                        onClick={() => setImageDialogOpen(false)}
                        sx={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            zIndex: 1,
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.45)",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.65)" }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={workoutImage?.imageData || ""}
                        alt="Workout full size"
                        sx={{
                            width: "100%",
                            maxHeight: "90vh",
                            objectFit: "contain",
                            backgroundColor: "black"
                        }}
                    />
                </Box>
            </Dialog>
        </>
    );
}

